import { useTheme, type DefaultTheme } from 'styled-components';

import { QuoteStatusEnum, type RFQProtocolEnum } from '../../types/types';
import { Box } from '../Core';
import { Icon, IconName, type IconProps } from '../Icons';
import { IndicatorDotVariants, IndicatorDotWrapper } from '../IndicatorDot';
import { Tooltip } from '../Tooltip';
import { Wrapper } from './styles';
import { QuoteStatusText } from './types';

const StatusIcon = ({
  quoteStatus,
  rfqProtocol,
  ...props
}: { quoteStatus: QuoteStatusEnum; rfqProtocol?: RFQProtocolEnum; theme?: DefaultTheme } & Omit<
  IconProps,
  'color' | 'icon'
>) => {
  const defaultTheme = useTheme();
  const theme = props.theme ?? defaultTheme;
  const { colorTextPositive, colorTextWarning, colorTextDefault } = theme;
  switch (quoteStatus) {
    case QuoteStatusEnum.PendingFix:
      return <Icon icon={IconName.CheckCircleSolid} color="var(--colors-blue-lighten)" {...props} />;
    case QuoteStatusEnum.Filled:
      return <Icon icon={IconName.CheckCircleSolid} color={colorTextPositive} {...props} />;
    case QuoteStatusEnum.Canceled:
    case QuoteStatusEnum.Closed:
      return <Icon icon={IconName.CloseCircleSolid} color={colorTextDefault} {...props} />;
    case QuoteStatusEnum.Rejected:
    case QuoteStatusEnum.Excluded:
      return <Icon icon={IconName.ExclamationCircleSolid} color={colorTextWarning} {...props} />;
    case QuoteStatusEnum.PendingNew:
      if (rfqProtocol === 'Manual') {
        // For new quotes that require Maker action (RFQ protocol Manual), we show the icon as Orange to draw attention
        return <Icon icon={IconName.CircleSolid} color="var(--colors-orange-lighten)" {...props} />;
      } else {
        return <Icon icon={IconName.Circle} color="var(--colors-blue-lighten)" {...props} />;
      }
    default:
      return <Icon icon={IconName.Circle} color="var(--colors-blue-lighten)" {...props} />;
  }
};

type QuoteStatusProps = {
  quoteStatus: QuoteStatusEnum;
  rfqProtocol?: RFQProtocolEnum;
  align?: 'right' | 'left';
  tooltipText?: string;
  theme?: DefaultTheme;
  iconPlacement?: 'right' | 'left';
};

const quoteStatusTextMapping = {
  [QuoteStatusEnum.PendingNew]: QuoteStatusText.PendingNew,
  [QuoteStatusEnum.Open]: QuoteStatusText.Open,
  [QuoteStatusEnum.PendingCancel]: QuoteStatusText.PendingCancel,
  [QuoteStatusEnum.Canceled]: QuoteStatusText.Canceled,
  [QuoteStatusEnum.PendingFill]: QuoteStatusText.Open,
  [QuoteStatusEnum.Filled]: QuoteStatusText.Filled,
  [QuoteStatusEnum.Closed]: QuoteStatusText.Rejected,
  [QuoteStatusEnum.Excluded]: QuoteStatusText.Rejected,
  [QuoteStatusEnum.Rejected]: QuoteStatusText.Rejected,
  [QuoteStatusEnum.Holding]: QuoteStatusText.Open,
  [QuoteStatusEnum.TransientError]: QuoteStatusText.Open,
  [QuoteStatusEnum.PendingFix]: QuoteStatusText.PendingFix,
} satisfies Record<QuoteStatusEnum, string>;

export function getQuoteStatusText({ quoteStatus }: { quoteStatus: QuoteStatusEnum }) {
  return quoteStatusTextMapping[quoteStatus];
}

export function QuoteStatus({
  quoteStatus,
  rfqProtocol,
  theme,
  align = 'right',
  tooltipText,

  ...props
}: QuoteStatusProps) {
  if (quoteStatus == null) {
    return null;
  }
  const quoteStatusText = getQuoteStatusText({ quoteStatus });

  const tooltipContent = !!tooltipText && <Box maxWidth="300px">{tooltipText}</Box>;

  return tooltipContent ? (
    <Tooltip tooltip={tooltipContent} usePortal>
      {renderQuoteStatus()}
    </Tooltip>
  ) : (
    renderQuoteStatus()
  );

  function renderQuoteStatus() {
    return (
      <Wrapper iconPlacement="left" align={align} title={tooltipText} theme={theme} {...props}>
        {quoteStatusText}
        <IndicatorDotWrapper show={!!tooltipText} theme={theme} variant={IndicatorDotVariants.Warning}>
          <StatusIcon quoteStatus={quoteStatus} rfqProtocol={rfqProtocol} theme={theme} size="fontSizeLarge" />
        </IndicatorDotWrapper>
      </Wrapper>
    );
  }
}

/** If you're rendering a CustomerQuote's status, then we also need the rfqProtocol. For simplicity however, we allow it to be undefined. But you have to specify it. */
export function CustomerQuoteStatus(props: QuoteStatusProps & { rfqProtocol: RFQProtocolEnum | undefined }) {
  return <QuoteStatus {...props} />;
}
